import { useQuery } from "@tanstack/react-query";
import {
  buildCombinedParams, fetchCombined, fetchCombinedAll, toDayParam,
  type CombinedBooking, type CombinedGuestFields, type CombinedQuery,
} from "../api/businessAccessCombined";
import {
  HIK_PIN_ATTENTION_STATUSES, DOOR_PIN_ATTENTION_WINDOW_HOURS, isDoorPinAttention, type HikPinStatus,
} from "../pages/invitation/hikPinStatus";

export type MainGuest = {
  id: number;
  name: string;
  last_name: string;
  tel?: string;
  email?: string;
  photo_url?: string | null;
  idnum?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  [key: string]: any;
} & CombinedGuestFields;

export type BusinessGuest = {
  id: number;
  name: string;
  last_name: string;
  tel?: string;
  email?: string;
  photo_url?: string | null;
  idnum?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  [key: string]: any;
} & CombinedGuestFields;

export type BusinessAccess = {
  id: string;
  business_id: string;
  business_name?: string;
  /** Residential invitations: the estate's community, building and name. */
  destination_group?: string | null;
  destination_building?: string | null;
  destination_estate?: string | null;
  /** Invitations: origin code (app / staff / import); business passes: channel name. */
  channel?: string | null;
  business_access_type_id: number;
  business_access_status_id: number;
  status_name?: string;
  /**
   * Backend-resolved access-type label (e.g. "Guest", "Client Booking").
   * `business_access_type_id` is NOT an id into `business_access_type` for
   * every row — for residential invitations it comes from the separate
   * `invitation_type` table (see AccessTypeControl.tsx's `kind` split) — so
   * this pre-resolved name, not a client-side id lookup, is the correct
   * label source. Sorted on already (Invitations.tsx's 'type_name' case).
   */
  type_name?: string;
  main_guest: MainGuest;
  business_guest?: BusinessGuest[];
  start_date: string;
  end_date: string;
  create_date: string;
  edit_date: string;
  children_count?: number | null;
  // SAM-1186: the pass's most recent charge (all null when it has none) and who created it (sam-lambda-functions #861).
  payment_status?: 'success' | 'voided' | 'refunded' | 'partially_refunded' | 'transferred' | null;
  payment_amount?: number | null;
  payment_currency?: string | null;
  payment_transaction_reference?: string | null;
  created_by_name?: string | null;
  // Entity field to distinguish data source
  entity?: 'business_access' | 'invitation';
  // Business information (for business_access records)
  business?: {
    id: string;
    name: string;
    estate_id?: number;
    estate?: {
      id: number;
      name: string;
      estate_group_id: number;
      estate_group?: {
        id: number;
        name: string;
      };
    };
  };
  // Estate information (for invitation records)
  estate?: {
    id: number;
    name: string;
    estate_group_id: number;
    estate_group?: {
      id: number;
      name: string;
    };
  };
  hik_pin?: string;
  hik_pass_id?: string;
  /** SAM-307/306/426 door PIN fields, all optional — see src/pages/invitation/hikPinStatus.ts. */
  hik_pin_status?: HikPinStatus | null;
  hik_pin_error?: string | null;
  hik_pin_attempts?: number | null;
  hik_pin_updated_at?: string | null;
  hik_open_count?: number | null;
  hik_use_count?: number | null;
  hik_first_used_at?: string | null;
  hik_valid_from?: string | null;
  hik_valid_until?: string | null;
  /** SAM-608: the booking that granted this pass (business_access rows only). */
  booking_id?: string | null;
  booking?: CombinedBooking | null;
  [key: string]: any;
};

export type BusinessAccessResponse = {
  data: BusinessAccess[];
  total_pages: number;
  page: number;
  limit?: number;
  total_elements: number;
};

/** Query for /business_access/combined — see src/api/businessAccessCombined.ts for the contract. */
export type BusinessInvitationParams = CombinedQuery;

// SAM-608: /combined now filters by business (business_id) and visit window (date_from /
// date_to) in SQL and paginates in SQL. It still has no _gte/_lte operators: any other key is a
// straight `column = value`, and a key outside [a-z0-9_] is a 400. buildCombinedParams() is the
// only place that turns a query into params, so a caller cannot send one the handler rejects.

/**
 * Hook para obtener invitaciones con paginación
 * @param params Filters and pagination (see CombinedQuery)
 * @param options.enabled false to hold the request (e.g. until a business id is known)
 * @returns Respuesta paginada de invitaciones
 */
export const useBusinessAccessInvitations = (
  params: Partial<BusinessInvitationParams> = {},
  options: { enabled?: boolean } = {},
) => {
  const query: CombinedQuery = { page: 1, limit: 20, ...params };
  const apiParams = buildCombinedParams(query);

  return useQuery<BusinessAccessResponse>({
    queryKey: ['business-access-invitations', apiParams],
    queryFn: () => fetchCombined<BusinessAccess>(query),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options.enabled ?? true,
  });
};

/**
 * Export (SAM-849): not a query. `fetchAll` walks /combined page by page (500 rows each) and
 * resolves with every row, or rejects with CombinedPageError if a page fails.
 */
export const useExportBusinessAccessInvitations = (params: Partial<BusinessInvitationParams> = {}) => {
  const query: CombinedQuery = { ...params };
  return {
    fetchAll: (onProgress?: (loaded: number, total: number) => void) =>
      fetchCombinedAll<BusinessAccess>(query, onProgress),
  };
};

/** Rows per status the "door PIN not OK" view asks for; the 48 h window keeps it small. */
const DOOR_PIN_ATTENTION_LIMIT = 500;

/**
 * SAM-307 — invitations whose door PIN is pending or failed and whose stay is in progress or
 * checks in within the next 48 h. /combined only has equality filters, so it asks once per
 * status (hik_pin_status=pending, hik_pin_status=failed) over the visit window
 * today .. today + 2 days — an overlap test on the server (COALESCE(end, start) >= date_from,
 * start < date_to + 1 day), so stays that began before today come back too — merges, and keeps
 * the rows isDoorPinAttention accepts (not ended, starting no later than now + 48 h).
 */
export const useDoorPinAttentionInvitations = (
  params: Pick<CombinedQuery, 'q' | 'estate_id'> = {},
  options: { enabled?: boolean } = {},
) => {
  const today = new Date();
  const until = new Date(today.getTime() + DOOR_PIN_ATTENTION_WINDOW_HOURS * 3600 * 1000);
  const base: CombinedQuery = {
    ...params,
    page: 1,
    limit: DOOR_PIN_ATTENTION_LIMIT,
    date_from: toDayParam(today),
    date_to: toDayParam(until),
  };

  return useQuery<BusinessAccess[]>({
    queryKey: ['business-access-invitations', 'door-pin-attention', buildCombinedParams(base)],
    queryFn: async () => {
      const pages = await Promise.all(
        HIK_PIN_ATTENTION_STATUSES.map((status) => fetchCombined<BusinessAccess>({ ...base, hik_pin_status: status })),
      );
      const seen = new Set<string>();
      const now = new Date();
      return pages
        .flatMap((p) => p?.data || [])
        .filter((row) => {
          const key = `${row.entity || ''}:${row.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return isDoorPinAttention(row, now);
        });
    },
    staleTime: 30 * 1000,
    enabled: options.enabled ?? true,
  });
};
